import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authApi, kpiApi } from './api';
import { setThresholds } from './semaphore';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await authApi.me();
      setUser(res.data);
      // Sincronizza le soglie del semaforo dall'utente con quelle del backend,
      // così tooltip/Hero chip/bar chart usano tutti la stessa banda gialla.
      // OPS-only (kpi/thresholds è blindato dalla 2026-05-21). Per AM/SU
      // restano i default hardcoded 5/-5 → semafori comunque renderizzati.
      if (res.data?.role === 'operations_manager') {
        try {
          const thr = await kpiApi.thresholds();
          const pct = (thr.data || []).find(r => r.threshold_type === 'percentage');
          if (pct) setThresholds({ green_min: pct.green_min, red_max: pct.red_max });
        } catch { /* silent: se non si caricano, restano i default 5/-5 */ }
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    // Public routes don't need an authenticated session — skip the network call
    // entirely so anonymous visitors don't see a flashed loader on /changelog.
    if (window.location.pathname.startsWith('/changelog')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await authApi.logout();
    } catch {}
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
